import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Github,
  Star,
  GitFork,
  FileText,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowUpDown,
} from "lucide-react";
import { useState } from "react";
import { api } from "../../services/api";
import { SpotlightCard } from "../ui/SpotlightCard";
import { Intelligence, ListFilter } from "../../types/intelligence";
import { cn } from "../../utils/cn";

interface AlphaFeedProps {
  onItemClick?: (item: Intelligence) => void;
}

// Helper function to check if item has GitHub repo
const hasGitHub = (item: Intelligence): boolean =>
  !!(item.github_url && item.github_url.trim() !== "");

const ITEMS_PER_PAGE = 20;

type GitHubFilter = "all" | "hasGitHub" | "noGitHub";
type SortOption = "score" | "time";

export function AlphaFeed({ onItemClick }: AlphaFeedProps) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [gitHubFilter, setGitHubFilter] = useState<GitHubFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("score");

  const isChinese = i18n.language === "zh";

  // Helper to get localized AI content (fallback to Chinese if English not available)
  const getLocalizedField = (
    zhField?: string,
    enField?: string,
  ): string | undefined => {
    if (isChinese) return zhField;
    return enField || zhField;
  };

  // Build filter object
  const buildFilter = (): ListFilter => {
    const filter: ListFilter = {
      page,
      limit: ITEMS_PER_PAGE,
      sort_by: sortBy,
    };
    if (keyword.trim()) {
      filter.keyword = keyword.trim();
    }
    if (gitHubFilter === "hasGitHub") {
      filter.has_github = true;
    } else if (gitHubFilter === "noGitHub") {
      filter.has_github = false;
    }
    return filter;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["intelligence", page, keyword, gitHubFilter, sortBy],
    queryFn: () => api.listIntelligence(buildFilter()),
  });

  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleSearch = () => {
    setKeyword(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (score >= 50) return "bg-blue-50 text-blue-700 border-blue-200";
    if (score >= 30) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="h-48 bg-white border border-slate-200 rounded-xl animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t("feed.search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {t("feed.searchBtn")}
            </button>
          </div>

          {/* GitHub Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={gitHubFilter}
              onChange={(e) => {
                setGitHubFilter(e.target.value as GitHubFilter);
                setPage(1);
              }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t("feed.filter.all")}</option>
              <option value="hasGitHub">{t("feed.filter.hasGitHub")}</option>
              <option value="noGitHub">{t("feed.filter.noGitHub")}</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortOption);
                setPage(1);
              }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="score">{t("feed.sort.score")}</option>
              <option value="time">{t("feed.sort.time")}</option>
            </select>
          </div>
        </div>

        {/* Total Count */}
        <div className="mt-2 text-sm text-slate-500">
          {t("feed.total", { total })}
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => {
          const hasRepo = hasGitHub(item);
          const summary = getLocalizedField(
            item.ai_summary,
            item.ai_summary_en,
          );

          // Use V3 CAV score
          const score = item.score_v3_cav;

          return (
            <SpotlightCard
              key={item.id}
              className="p-4 cursor-pointer"
              spotlightColor="rgba(59, 130, 246, 0.1)"
              onClick={() => onItemClick?.(item)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {/* V3 Score Badge */}
                  <span
                    className={cn(
                      "px-2 py-1 rounded-lg text-sm font-bold num",
                      getScoreColor(score),
                    )}
                  >
                    {score.toFixed(1)}
                  </span>

                  {/* Only show license badge if has GitHub */}
                  {hasRepo && item.repo_license && (
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                      {item.repo_license}
                    </span>
                  )}
                  {/* Show paper-only badge if no GitHub */}
                  {!hasRepo && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {t("drawer.paperOnly")}
                    </span>
                  )}
                </div>
                {/* Only show GitHub icon if has repo */}
                {hasRepo && (
                  <a
                    href={item.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-slate-600"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Github className="w-4 h-4" />
                  </a>
                )}
              </div>

              <h3 className="text-sm font-medium text-slate-800 mb-2 line-clamp-2">
                {hasRepo ? item.repo_name : item.paper_title}
              </h3>

              <p className="text-sm text-slate-600 mb-3 line-clamp-3">
                {summary || item.repo_description || t("drawer.noDescription")}
              </p>

              <div className="flex items-center justify-between text-xs text-slate-500">
                {/* Only show stars/forks if has GitHub */}
                {hasRepo ? (
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      {item.repo_stars.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <GitFork className="w-3 h-3" />
                      {item.repo_forks}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-amber-600">
                    <FileText className="w-3 h-3" />
                    <span>{t("drawer.noGitHub")}</span>
                  </div>
                )}
                {/* Analysis Time */}
                {item.mined_at && (
                  <span className="flex items-center gap-1 text-slate-400">
                    <Clock className="w-3 h-3" />
                    {item.mined_at.split("T")[0]}
                  </span>
                )}
              </div>
            </SpotlightCard>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={cn(
              "flex items-center gap-1 px-4 py-2 rounded-lg text-sm",
              page === 1
                ? "text-slate-400 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            {t("feed.prev")}
          </button>

          <span className="text-sm text-slate-600">
            {page} / {totalPages}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={cn(
              "flex items-center gap-1 px-4 py-2 rounded-lg text-sm",
              page === totalPages
                ? "text-slate-400 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {t("feed.next")}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-12">
          <Github className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{t("feed.noData")}</p>
          {keyword && (
            <p className="text-sm text-slate-400 mt-2">
              {t("feed.noResults", { keyword })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
